import { localizedStrings } from '@core/strings'
import React, { useState } from 'react'
import { useModal } from '@common/GlobalModal'
import {
  DialogActions,
  DialogTitle,
  IconButton,
  DialogContent,
  Button,
  TextField,
  LoadingButton,
} from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Text } from '@fundamentals'
import { ModalDialogContainer } from '@common/modal'
import { permitKeys } from '@core/react-query/features/permits/permit'
import { useIsMutating } from '@tanstack/react-query'

interface ComponentType {
  organisationId: number
  onSaveDraft: (draftName: string) => void
  onDismiss?: () => void
  draftName?: string
}

export const SaveDraftModal: React.FC<ComponentType> = ({
  organisationId,
  onSaveDraft,
  onDismiss,
  draftName: draftNameProp,
}) => {
  const { hideModal } = useModal()
  const [draftName, setDraftName] = useState(draftNameProp)
  const isCreatingPermitDraft = useIsMutating({
    mutationKey: permitKeys.createDraft.mutationKey,
  })

  const isUpdatingPermitDraft = useIsMutating({
    mutationKey: permitKeys.updateDraft.mutationKey,
  })

  const handleSaveDraft = () => {
    onSaveDraft(draftName)
  }

  const onDismissClick = () => {
    onDismiss && onDismiss()
    hideModal()
  }

  return (
    <ModalDialogContainer>
      <DialogTitle
        variant='h5'
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        sx={{ pr: 1, fontSize: 20 }}
      >
        {localizedStrings.draftName}
        <IconButton aria-label='close-modal' onClick={onDismissClick}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Text>{localizedStrings.saveDraftDescription}</Text>
        <TextField
          inputProps={{ 'data-test': 'draft-name-input' }}
          label={localizedStrings.note}
          placeholder={localizedStrings.enterNote}
          fullWidth
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions sx={{ pb: 2, pr: 3 }}>
        <Button
          variant='outlined'
          onClick={onDismissClick}
          data-test='confirm-no'
        >
          {localizedStrings.cancel}
        </Button>
        <LoadingButton
          data-test='confirm-yes'
          variant='contained'
          onClick={handleSaveDraft}
          disabled={!draftName?.length}
          loading={!!isCreatingPermitDraft || !!isUpdatingPermitDraft}
        >
          {draftNameProp
            ? localizedStrings.updateDraft
            : localizedStrings.saveDraft}
        </LoadingButton>
      </DialogActions>
    </ModalDialogContainer>
  )
}
